import { CLIENT_PAYMENT_ERRORS, type LedgerDirection } from '@carsfinders/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';

import { Button } from '../../../../components/ui';
import { t } from '../../../../lib/i18n';
import { toast } from '../../../../lib/toast';
import { FINANCE_DEPOSITS_SEARCH } from '../../../finance/components/finance-view-switch';
import { apiErrorCode, recordDepositAdjustment } from '../../api';
import { parseAmount } from './allocation';
import { invalidateClientMoney } from './finance-format';
import {
  DialogShell,
  FormAlert,
  cardClass,
  fieldClass,
  headingClass,
  labelClass,
  textareaClass,
} from './finance-ui';

/** `DepositAdjustmentReasonSchema`: optional, at most 500 characters. */
const REASON_MAX = 500;

/**
 * «Коригування» — a rare correction of a bookkeeping error, NOT a way to record
 * money received (that is «Додати платіж»). The reason is optional (owner,
 * 2026-10-25); when given it lands in the `admin.deposit_adjusted` audit row; a debit
 * beyond the deposit is refused by the server (`DEPOSIT_INSUFFICIENT`).
 */
function AdjustmentDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const nameId = useId();
  const amountRef = useRef<HTMLInputElement>(null);
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<LedgerDirection>('credit');
  const [reason, setReason] = useState('');

  useEffect(() => {
    amountRef.current?.focus();
  }, []);

  const amountChf = parseAmount(amount);
  const amountValid = Number.isFinite(amountChf) && amountChf > 0;
  const reasonLength = reason.trim().length;
  const reasonValid = reasonLength <= REASON_MAX;

  const mutation = useMutation({
    mutationFn: () =>
      recordDepositAdjustment(userId, {
        amountChf,
        direction,
        ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success(t('clients.finance.adjust.recorded'));
      invalidateClientMoney(queryClient, userId);
      onClose();
    },
  });
  const busy = mutation.isPending;

  const errorCode = mutation.isError ? apiErrorCode(mutation.error) : undefined;
  const errorText = !mutation.isError
    ? null
    : errorCode === CLIENT_PAYMENT_ERRORS.depositInsufficient
      ? t('clients.finance.adjust.errors.insufficient')
      : errorCode
        ? t('clients.finance.adjust.errors.failedCode', { code: errorCode })
        : t('clients.finance.adjust.errors.failed');

  return (
    <DialogShell
      title={t('clients.finance.adjust.title')}
      busy={busy}
      onClose={onClose}
      testId="adjust-dialog"
    >
      <p className="m-0 text-[12px] leading-[1.5] text-cf-ink-subtle">
        {t('clients.finance.adjust.hint')}
      </p>
      <label className={labelClass}>
        {t('clients.finance.adjust.amount')}
        <input
          ref={amountRef}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          disabled={busy}
          aria-invalid={amount !== '' && !amountValid}
          data-testid="adjust-amount"
          className={`${fieldClass} font-mono`}
        />
        {amount !== '' && !amountValid && (
          <span className="text-[12px] text-cf-danger">
            {t('clients.finance.adjust.errors.invalidAmount')}
          </span>
        )}
      </label>
      <fieldset className="m-0 flex flex-wrap gap-4 border-0 p-0">
        <legend className="mb-1.5 p-0 text-[13px] text-cf-ink-muted">
          {t('clients.finance.adjust.direction')}
        </legend>
        {(['credit', 'debit'] as const).map((value) => (
          <label key={value} className="inline-flex items-center gap-2 text-[14px] text-cf-ink">
            <input
              type="radio"
              name={`${nameId}-direction`}
              value={value}
              checked={direction === value}
              onChange={() => setDirection(value)}
              disabled={busy}
              data-testid={`adjust-direction-${value}`}
              className="h-4 w-4 accent-cf-brand"
            />
            {t(`clients.finance.adjust.directions.${value}`)}
          </label>
        ))}
      </fieldset>
      <label className={labelClass}>
        <span className="flex items-baseline justify-between gap-2">
          <span>{t('clients.finance.adjust.reason')}</span>
          <span
            data-testid="adjust-counter"
            className={`font-mono text-[12px] ${reasonValid || reasonLength === 0 ? 'text-cf-ink-subtle' : 'text-cf-danger'}`}
          >
            {t('clients.finance.adjust.counter', { count: String(reasonLength) })}
          </span>
        </span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={REASON_MAX}
          rows={3}
          disabled={busy}
          aria-invalid={reasonLength > 0 && !reasonValid}
          data-testid="adjust-reason"
          className={textareaClass}
        />
        <span className="text-[12px] text-cf-ink-subtle">
          {t('clients.finance.adjust.reasonHint')}
        </span>
      </label>
      {errorText && <FormAlert testId="adjust-error">{errorText}</FormAlert>}
      <div className="mt-1 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
          {t('clients.finance.adjust.cancel')}
        </Button>
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!amountValid || !reasonValid || busy}
          data-testid="adjust-submit"
        >
          {busy ? t('clients.finance.adjust.submitting') : t('clients.finance.adjust.submit')}
        </Button>
      </div>
    </DialogShell>
  );
}

/**
 * «Дії з депозитом»: Коригування (dialog, mandatory reason) and a pointer to
 * «Депозити й режими», where a refund is recorded with its own checks.
 */
export function DepositActions({ userId }: { userId: string }) {
  const headingId = useId();
  const [adjusting, setAdjusting] = useState(false);
  return (
    <section
      aria-labelledby={headingId}
      data-testid="finance-deposit-actions"
      className={cardClass}
    >
      <h2 id={headingId} className={headingClass}>
        {t('clients.finance.actions.title')}
      </h2>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => setAdjusting(true)}
          data-testid="finance-adjust"
          className="self-start"
        >
          {t('clients.finance.actions.adjust')}
        </Button>
        <p className="m-0 text-[13px] leading-[1.5] text-cf-ink-muted">
          {t('clients.finance.actions.refundHint')}{' '}
          <Link
            to="/finance"
            search={FINANCE_DEPOSITS_SEARCH}
            data-testid="finance-refund-link"
            className="font-semibold text-cf-ink underline underline-offset-2 hover:text-cf-brand focus-visible:outline-2 focus-visible:outline-cf-ink"
          >
            {t('clients.finance.actions.refundLink')}
          </Link>
        </p>
      </div>
      {adjusting && <AdjustmentDialog userId={userId} onClose={() => setAdjusting(false)} />}
    </section>
  );
}
